import { useMemo } from 'react';
import DashboardCard, { EmptyState } from './DashboardCard';
import CardGrid from './CardGrid';
import { cardDateColumn, type CardColumn } from './cardColumns';
import { useGlobalStore } from '../../stores/globalStore';
import type { DashboardDischargedResident } from '../../types/dashboard';
import { useDashboardNav } from './useDashboardNav';

interface Props {
  data: DashboardDischargedResident[];
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function DischargedResidentsCard({ data, ...dragProps }: Props) {
  const { rowLinkProps } = useDashboardNav();
  const roomLabel = useGlobalStore((s) => s.roomLabel);
  const plural = useGlobalStore((s) => s.residentPluralLabel);
  const title = `Discharged ${plural}`;

  const columns = useMemo<CardColumn<DashboardDischargedResident>[]>(() => [
    { dataField: 'fullName', caption: 'Name', sizing: 'fill' },
    { dataField: 'unit', caption: 'Unit' },
    {
      name: 'roomBed',
      caption: roomLabel,
      calculateCellValue: (row) => `${row.room_No ?? ''} ${row.bed_No ?? ''}`.trim(),
    },
    cardDateColumn<DashboardDischargedResident>('dateTimeOfLog', 'Date', { sortOrder: 'desc' }),
    { dataField: 'isDeleted', caption: 'Deleted', dataType: 'boolean' },
  ], [roomLabel]);

  return (
    <DashboardCard icon="bi-person-dash" title={title} count={data.length} {...dragProps}>
      {data.length === 0 ? (
        <EmptyState message={`No discharged ${plural.toLowerCase()}`} />
      ) : (
        <CardGrid<DashboardDischargedResident>
          {...rowLinkProps<DashboardDischargedResident>((r) => r.pKey_Patient)}
          ariaLabel={title}
          dataSource={data}
          columns={columns}
          keyExpr={(r) => `${r.pKey_Patient}|${r.dateTimeOfLog}`}
        />
      )}
    </DashboardCard>
  );
}
